<template>
  <BannerLayout
    bg-color="background-disabled"
    v-if="
      (isIncognito || (!isInitialLoadingDirectConsultations && (isEndingSoon || isShiftEnded))) &&
      (!isCloseButtonVisible || isBannerVisible)
    "
  >
    <template #text>
      <span class="text-h6 text-subtitle">
        {{ bannerMessage }}
      </span>
    </template>
    <template #actions>
      <VBtn
        v-if="isShiftEnded && areReportsPending"
        data-testid="write-consultation-reports-btn"
        class="button-large"
        color="primary"
        :to="{ name: routeNames.CONSULTATIONS }"
        :active="false"
      >
        {{ t('Write consultation reports') }}
      </VBtn>
      <VBtn
        v-if="isEndingSoon || isIncognito"
        data-testid="change-shift-btn"
        class="button-large"
        color="primary"
        :to="{ name: routeNames.USER_PROFILE }"
        :active="false"
      >
        {{ t('Change shift') }}
      </VBtn>
      <VBtn
        v-if="isCloseButtonVisible"
        icon="close"
        variant="plain"
        size="x-small"
        :ripple="false"
        data-testid="btn-close"
        @click="closeBanner"
      />
    </template>
  </BannerLayout>
</template>

<script setup lang="ts">
import BannerLayout from '@/atoms/BannerLayout.vue'
import { useI18n } from 'vue-i18n'
import { routeNames } from '@/router/routeNames'
import { computed, onMounted, ref } from 'vue'
import { useUserStore } from '@/stores/user'
import { UserSettingsService } from '@/services/telehealthApi/UserSettingsService'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import { storeToRefs } from 'pinia'

import { useConsultationRequestStore } from '@/stores/consultationRequest'
import { useShift } from '@/composables/useShift'

const { t } = useI18n()

const userStore = useUserStore()
const {
  areReportsPending,
  isInitialLoadingIncomingDirectConsultations,
  isInitialLoadingOutgoingDirectConsultations
} = storeToRefs(useConsultationRequestStore())

const { timeToEnd, isShiftEnded, isIncognito, isEndingSoon } = useShift()

const isBannerVisible = ref(true)

const isInitialLoadingDirectConsultations = computed(
  () =>
    isInitialLoadingIncomingDirectConsultations.value ||
    isInitialLoadingOutgoingDirectConsultations.value
)

const isCloseButtonVisible = computed(
  () => isIncognito.value || isEndingSoon.value || (isShiftEnded.value && !areReportsPending.value)
)

const bannerMessage = computed(() => {
  if (isIncognito.value)
    return t(
      'You do not have a shift selected and only receive requests for scheduled consultations.'
    )

  if (!isShiftEnded.value) return `${t('Your shift ends in')} ${timeToEnd.value}`

  if (areReportsPending.value) {
    return t(
      'Your shift has ended. The status "no shift" has been set automatically and you do not receive consultation requests. Finalize pending consultation reports before you log out.'
    )
  }

  return t(
    'Your shift has ended. The status "no shift" has been set automatically and you do not receive consultation requests. There are no pending consultation requests or consultation reports, and you can log out.'
  )
})

async function fetchShiftEndTime() {
  if (!userStore.user?.userName) return

  try {
    const response = await UserSettingsService.getUserSettings(userStore.user.userName)

    userStore.setEndOfShiftDate(response.userSettings.shift?.end)
  } catch (error) {
    useNoticeStore().add({
      type: NoticeType.ERROR,
      message: t('Failed to fetch shift end time')
    })
  }
}

function closeBanner() {
  isBannerVisible.value = false
}

onMounted(async () => {
  await fetchShiftEndTime()
})
</script>
